// src/pages/rankings/AthleteProfile.tsx
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "../../hooks/useTranslation";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import PublicHeader from "../../components/PublicHeader";
import { C, FONT } from "../../theme";
import { getResultsByAthlete, RankingResult } from "../../firebase/rankings";
import { getPublicAthleteByFederationId, getPublicAthleteBySlug } from "../../firebase/publicDirectory";
import { placeLabel } from "../../utils/rankingCalculator";
import { getPublicRankingContributions } from "../../firebase/rankingLifecycle";

// ── Responsive hook ──────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

// ── Icons ────────────────────────────────────────────────────────────────────
const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const EventsIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const ClubIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);
const TrophyIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9H4a2 2 0 0 1-2-2V5h4" /><path d="M18 9h2a2 2 0 0 0 2-2V5h-4" />
    <path d="M12 17v4" /><path d="M8 21h8" /><path d="M6 5v4a6 6 0 0 0 12 0V5H6z" />
  </svg>
);
const UserIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);
const StarIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

// ── Geo flag SVG ─────────────────────────────────────────────────────────────
const GeoFlag = () => (
  <svg width="20" height="14" viewBox="0 0 20 14" xmlns="http://www.w3.org/2000/svg" style={{ borderRadius: 2, flexShrink: 0 }}>
    <rect width="20" height="14" fill="#fff" />
    {/* red cross */}
    <rect x="8.5" y="0" width="3" height="14" fill="#da291c" />
    <rect x="0" y="5.5" width="20" height="3" fill="#da291c" />
    {/* four small crosses */}
    <rect x="1.5" y="1.5" width="1.2" height="3.5" fill="#da291c" />
    <rect x="0.5" y="2.4" width="3.2" height="1.2" fill="#da291c" />
    <rect x="15.3" y="1.5" width="1.2" height="3.5" fill="#da291c" />
    <rect x="14.3" y="2.4" width="3.2" height="1.2" fill="#da291c" />
    <rect x="1.5" y="9" width="1.2" height="3.5" fill="#da291c" />
    <rect x="0.5" y="10" width="3.2" height="1.2" fill="#da291c" />
    <rect x="15.3" y="9" width="1.2" height="3.5" fill="#da291c" />
    <rect x="14.3" y="10" width="3.2" height="1.2" fill="#da291c" />
  </svg>
);

// ── RankBadge (matches RankingsPage) ─────────────────────────────────────────
function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) {
    return (
      <div style={{
        width: 28, height: 28, borderRadius: "50%",
        background: "linear-gradient(135deg, #ffd700, #f59e0b)",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 0 10px rgba(245,158,11,0.4)",
        fontSize: 11, fontWeight: 800, color: "#78350f", fontFamily: FONT.body, flexShrink: 0,
      }}>1</div>
    );
  }
  if (rank === 2) {
    return (
      <div style={{
        width: 28, height: 28, borderRadius: "50%",
        background: "linear-gradient(135deg, #e2e8f0, #94a3b8)",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 0 8px rgba(148,163,184,0.3)",
        fontSize: 11, fontWeight: 800, color: "var(--color-text)", fontFamily: FONT.body, flexShrink: 0,
      }}>2</div>
    );
  }
  if (rank === 3) {
    return (
      <div style={{
        width: 28, height: 28, borderRadius: "50%",
        background: "linear-gradient(135deg, #fb923c, #c2410c)",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 0 8px rgba(251,146,60,0.35)",
        fontSize: 11, fontWeight: 800, color: C.text, fontFamily: FONT.body, flexShrink: 0,
      }}>3</div>
    );
  }
  return (
    <div style={{
      width: 28, height: 28, borderRadius: "50%",
      background: C.surface2,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 11, fontWeight: 700, color: C.text2,
      fontFamily: FONT.body, flexShrink: 0,
    }}>{rank}</div>
  );
}

// ── Avatar component ─────────────────────────────────────────────────────────
function ProfileAvatar({ firstName, lastName, imageUrl, size }: {
  firstName: string; lastName: string; imageUrl?: string; size: number;
}) {
  const initials = `${(firstName ?? "").charAt(0)}${(lastName ?? "").charAt(0)}`.toUpperCase();
  if (imageUrl) {
    return (
      <img src={imageUrl} alt={`${firstName} ${lastName}`}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover",
          border: "3px solid rgba(218,41,28,0.5)", flexShrink: 0 }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: "linear-gradient(135deg, rgba(218,41,28,0.2), rgba(218,41,28,0.05))",
      border: "3px solid rgba(218,41,28,0.35)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: FONT.display, fontSize: size * 0.38, letterSpacing: "2px",
      color: C.text, flexShrink: 0,
    }}>
      {initials || "?"}
    </div>
  );
}

// ── Skeleton loader ───────────────────────────────────────────────────────────
function SkeletonLine({ w, h = 10 }: { w: string | number; h?: number }) {
  return (
    <div style={{
      width: w, height: h, borderRadius: 6,
      background: "linear-gradient(90deg, var(--color-border) 25%, var(--color-border-strong) 50%, var(--color-border) 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.4s infinite",
    }} />
  );
}

const CURRENT_YEAR = new Date().getFullYear();

// ── Main Component ────────────────────────────────────────────────────────────
const AthleteProfile: React.FC = () => {
  const { athleteId, athleteSlug } = useParams<{ athleteId?: string; athleteSlug?: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const w = useWindowWidth();
  const isMobile = w < 768;

  const [results, setResults] = useState<RankingResult[]>([]);
  const [athleteInfo, setAthleteInfo] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [rankingProvenance, setRankingProvenance] = useState<any[]>([]);

  // True when the URL points to an unknown athleteId: no profile doc AND
  // no ranking-result rows. Drives a friendly empty state instead of the
  // misleading "—" placeholders that previously rendered.
  const notFound = !loading && !athleteInfo;

  useEffect(() => { const id = setTimeout(() => setHeroLoaded(true), 80); return () => clearTimeout(id); }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const info = athleteSlug
          ? await getPublicAthleteBySlug(athleteSlug)
          : await getPublicAthleteByFederationId(athleteId || "");
        setAthleteInfo(info ?? null);
        if (!info) { setResults([]); return; }
        setResults(await getResultsByAthlete(info.athleteId) as RankingResult[]);
        if (athleteId && info.slug) navigate(`/athletes/${info.slug}`, { replace: true });
      } catch (err) {
        console.error("AthleteProfile load error:", err);
        setAthleteInfo(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [athleteId, athleteSlug, navigate]);

  useEffect(() => {
    const publishedAthleteId = athleteInfo?.athleteId;
    if (!publishedAthleteId) { setRankingProvenance([]); return; }
    let active = true;
    // Additive collection: a missing rule/function deployment must not break
    // existing athlete profiles during the staged rollout.
    getPublicRankingContributions(publishedAthleteId)
      .then((rows) => { if (active) setRankingProvenance(rows); })
      .catch((err) => { console.info("Ranking provenance is not available yet", err); if (active) setRankingProvenance([]); });
    return () => { active = false; };
  }, [athleteInfo?.athleteId]);

  const currentYearResults  = results.filter((r) => Number(r.year) === CURRENT_YEAR);
  const previousYearResults = results.filter((r) => Number(r.year) === CURRENT_YEAR - 1);
  const olderResults        = results.filter((r) => Number(r.year) < CURRENT_YEAR - 1);
  const currentYearPoints   = currentYearResults.reduce((s, r) => s + r.pointsAwarded, 0);
  const previousYearPoints  = previousYearResults.reduce((s, r) => s + r.pointsAwarded, 0);
  const bestPlace = results.reduce((best, r) => {
    if (r.place !== null && (best === null || r.place < best)) return r.place;
    return best;
  }, null as number | null);

  // Set of distinct years older than (current - 1), sorted desc.
  // Used to render one ResultTable per older year inside the toggle.
  const olderYears = Array.from(new Set(olderResults.map((r) => Number(r.year))))
    .filter((y) => Number.isFinite(y))
    .sort((a, b) => b - a);
  const [showOlder, setShowOlder] = useState(false);

  const firstName   = athleteInfo?.firstName ?? results[0]?.athleteFirstName ?? "";
  const lastName    = athleteInfo?.lastName  ?? results[0]?.athleteLastName  ?? "";
  const displayName = `${firstName} ${lastName}`.trim();
  const clubName    = athleteInfo?.currentClub || "—";
  const imageUrl    = athleteInfo?.imageUrl ?? "";

  const formatDate = (r: RankingResult) => `${String(r.month).padStart(2, "0")}/${r.year}`;

  // ── Result Table sub-component (dark styled) ─────────────────────────────
  const ResultTable = ({ rows, label, year }: { rows: RankingResult[]; label: string; year: number }) => (
    <div style={{ marginBottom: 32 }}>
      {/* Section header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        marginBottom: 12, paddingBottom: 12,
        borderBottom: `1px solid ${C.border}`,
      }}>
        <div style={{
          width: 3, height: 18, borderRadius: 2,
          background: "linear-gradient(180deg, #da291c, rgba(218,41,28,0.3))",
          flexShrink: 0,
        }} />
        <span style={{
          fontFamily: FONT.display, fontSize: 20, letterSpacing: "1px", color: C.text,
        }}>{label}</span>
        {rows.length > 0 && (
          <span style={{
            marginLeft: "auto", fontSize: 11, fontWeight: 700,
            color: C.text3, backgroundColor: C.surface2,
            border: `1px solid ${C.border}`, borderRadius: 20,
            padding: "2px 10px", fontFamily: FONT.body,
          }}>{rows.length} result{rows.length !== 1 ? "s" : ""}</span>
        )}
      </div>

      {rows.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "48px 20px",
          backgroundColor: C.surface, borderRadius: 14,
          border: `1px solid ${C.border}`,
        }}>
          <div style={{ color: "var(--color-border)", marginBottom: 12, display: "flex", justifyContent: "center" }}>
            <TrophyIcon />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text3, fontFamily: FONT.body, marginBottom: 4 }}>
            {t("ranking.profile.noData")}
          </div>
          <div style={{ fontSize: 12, color: C.text3, fontFamily: FONT.body }}>
            No competition results for {year}
          </div>
        </div>
      ) : (
        <div style={{ overflowX: "auto", backgroundColor: C.surface, border: `1px solid ${C.border}`, borderRadius: 14 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}`, backgroundColor: "var(--color-surface-2)" }}>
                {(isMobile
                  ? ["Competition", t("ranking.table.points")]
                  : ["Competition", "Date", t("ranking.profile.weightClass"), "Age Group", "Place", t("ranking.table.points")]
                ).map((h, i) => (
                  <th key={h} style={{
                    textAlign: i === (isMobile ? 1 : 5) ? "right" : "left",
                    padding: "10px 14px",
                    fontSize: 9, fontWeight: 800, letterSpacing: "1px",
                    color: C.text3, textTransform: "uppercase",
                    fontFamily: FONT.body, whiteSpace: "nowrap",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.id} style={{
                  borderBottom: `1px solid ${C.border}`,
                  backgroundColor: idx % 2 === 0 ? "transparent" : "var(--color-surface-2)",
                }}>
                  {isMobile ? (
                    <>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: FONT.body, lineHeight: 1.3 }}>
                          {r.competitionName}
                        </div>
                        <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                          <span style={{
                            fontSize: 10, padding: "1px 7px",
                            backgroundColor: "rgba(59,130,246,0.1)", color: "rgba(96,165,250,0.8)",
                            borderRadius: 20, fontFamily: FONT.body, fontWeight: 600,
                            border: "1px solid rgba(59,130,246,0.2)",
                          }}>{r.weightClass}</span>
                          <span style={{
                            fontSize: 10, padding: "1px 7px",
                            backgroundColor: C.surface2, color: C.text3,
                            borderRadius: 20, fontFamily: FONT.body, fontWeight: 600,
                            border: `1px solid ${C.border}`,
                          }}>{r.ageGroup}</span>
                          <span style={{
                            fontSize: 10, padding: "1px 7px",
                            backgroundColor: C.surface2, color: C.text3,
                            borderRadius: 20, fontFamily: FONT.body, fontWeight: 600,
                            border: `1px solid ${C.border}`,
                          }}>{placeLabel(r.place)}</span>
                        </div>
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "right", verticalAlign: "middle" }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: C.red, fontFamily: FONT.display, letterSpacing: "0.5px", lineHeight: 1 }}>
                          {r.pointsAwarded}
                        </div>
                        <div style={{ fontSize: 9, color: C.text3, fontFamily: FONT.body, marginTop: 2 }}>pts</div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding: "13px 14px" }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: FONT.body, lineHeight: 1.3 }}>
                          {r.competitionName}
                        </div>
                        <div style={{ fontSize: 11, color: C.text3, fontFamily: FONT.body, marginTop: 2 }}>
                          {formatDate(r)}
                        </div>
                      </td>
                      <td style={{ padding: "13px 14px", fontSize: 12, color: C.text2, fontFamily: FONT.body }}>
                        {formatDate(r)}
                      </td>
                      <td style={{ padding: "13px 14px" }}>
                        <span style={{
                          fontSize: 10, padding: "2px 8px",
                          backgroundColor: "rgba(59,130,246,0.1)", color: "rgba(96,165,250,0.8)",
                          borderRadius: 20, fontFamily: FONT.body, fontWeight: 600,
                          border: "1px solid rgba(59,130,246,0.2)",
                        }}>{r.weightClass}</span>
                      </td>
                      <td style={{ padding: "13px 14px", fontSize: 12, color: C.text2, fontFamily: FONT.body }}>
                        {r.ageGroup}
                      </td>
                      <td style={{ padding: "13px 14px" }}>
                        <div style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          fontSize: 12, fontWeight: 600, color: C.text2,
                          fontFamily: FONT.body,
                        }}>
                          <RankBadge rank={r.place ?? 99} />
                          {placeLabel(r.place)}
                        </div>
                      </td>
                      <td style={{ padding: "13px 14px", textAlign: "right" }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: C.red, fontFamily: FONT.display, letterSpacing: "0.5px", lineHeight: 1 }}>
                          {r.pointsAwarded}
                        </div>
                        <div style={{ fontSize: 9, color: C.text3, fontFamily: FONT.body, marginTop: 2, textAlign: "right" }}>pts</div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
    <Helmet>
      <title>{firstName || lastName ? `${displayName} - Athlete Profile — Georgian Taekwondo Federation` : "Athlete Profile — Georgian Taekwondo Federation"}</title>
      <meta name="description" content="View competition results, ranking points, and achievements for this athlete." />
      <link rel="canonical" href={`https://registration.taekwondo.ge/athletes/${athleteInfo?.slug || athleteSlug || ""}`} />
      <meta property="og:title" content={firstName || lastName ? `${displayName} - Athlete Profile — Georgian Taekwondo Federation` : "Athlete Profile — Georgian Taekwondo Federation"} />
      <meta property="og:description" content="View competition results, ranking points, and achievements for this athlete." />
      <meta property="og:url" content={`https://registration.taekwondo.ge/athletes/${athleteInfo?.slug || athleteSlug || ""}`} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://registration.taekwondo.ge/geo.png" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={firstName || lastName ? `${displayName} - Athlete Profile — Georgian Taekwondo Federation` : "Athlete Profile — Georgian Taekwondo Federation"} />
      <meta name="twitter:description" content="View competition results, ranking points, and achievements for this athlete." />
      <meta name="twitter:image" content="https://registration.taekwondo.ge/geo.png" />
      <link rel="alternate" hrefLang="en" href={`https://registration.taekwondo.ge/athletes/${athleteInfo?.slug || athleteSlug || ""}`} />
      <link rel="alternate" hrefLang="ka" href={`https://registration.taekwondo.ge/athletes/${athleteInfo?.slug || athleteSlug || ""}`} />
      <link rel="alternate" hrefLang="x-default" href={`https://registration.taekwondo.ge/athletes/${athleteInfo?.slug || athleteSlug || ""}`} />
    </Helmet>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Figtree:wght@400;500;600;700;800&display=swap');

        @keyframes heroBgZoom {
          from { transform: scale(1.07); }
          to   { transform: scale(1); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes shimmer {
          0%   { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        .ap-loaded .ap-a0 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.05s both; }
        .ap-loaded .ap-a1 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.15s both; }
        .ap-loaded .ap-a2 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.25s both; }
        .ap-loaded .ap-a3 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.35s both; }
        .ap-loaded .ap-a4 { animation: fadeUp 0.65s cubic-bezier(.22,1,.36,1) 0.45s both; }

        .ap-back:hover { color: var(--color-text) !important; border-color: var(--color-text-subtle) !important; }
        .ap-nav-link:hover { color: var(--color-accent) !important; }
        .ap-nav-cta:hover { background: rgba(218,41,28,0.85) !important; }

        * { box-sizing: border-box; }
      `}</style>

      <div style={{ minHeight: "100vh", backgroundColor: C.bg, fontFamily: FONT.body }}>

        {/* ══════════ NAVBAR ════════════════════════════════════════════════ */}
        <PublicHeader backTo="/athletes" backLabel={t("nav.athletes")} />
        {false && <header style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          height: 56, padding: isMobile ? "0 14px" : "0 32px",
          backgroundColor: "rgba(7,10,15,0.95)",
          backdropFilter: "blur(12px)",
          borderBottom: `1px solid ${C.border}`,
          position: "sticky", top: 0, zIndex: 100,
        }}>
          <button
            className="ap-back"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "none", border: `1px solid ${C.border2}`,
              borderRadius: 8, padding: "5px 11px", cursor: "pointer",
              color: C.text2, fontFamily: FONT.body,
              fontSize: 12, fontWeight: 600, letterSpacing: "0.4px",
              transition: "color 0.15s, border-color 0.15s",
            }}
            onClick={() => navigate("/rankings")}
          >
            <BackIcon />
            {!isMobile && t("common.back")}
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/geo.png" alt="Logo" style={{ width: 30, height: 30, objectFit: "contain" }} />
            {!isMobile && (
              <div>
                <div style={{ fontFamily: FONT.display, fontSize: 14, letterSpacing: "3px", color: C.text, lineHeight: 1.1 }}>GEORGIAN TAEKWONDO</div>
                <div style={{ fontFamily: FONT.display, fontSize: 9, letterSpacing: "2.5px", color: C.text3, lineHeight: 1 }}>NATIONAL FEDERATION</div>
              </div>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 10 }}>
            <button
              className="ap-nav-link"
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                background: "none", border: "none", cursor: "pointer",
                color: C.text2, fontSize: 12, fontWeight: 600,
                fontFamily: FONT.body, letterSpacing: "0.3px", transition: "color 0.15s",
                padding: "4px 6px",
              }}
              onClick={() => navigate("/events")}
            >
              <EventsIcon />
              {!isMobile && <span>{t("landing.events")}</span>}
            </button>
            <button
              className="ap-nav-link"
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                background: "none", border: "none", cursor: "pointer",
                color: C.text2, fontSize: 12, fontWeight: 600,
                fontFamily: FONT.body, letterSpacing: "0.3px", transition: "color 0.15s",
                padding: "4px 6px",
              }}
              onClick={() => navigate("/rankings/clubs")}
            >
              <ClubIcon />
              {!isMobile && <span>{t("ranking.clubRankings.title")}</span>}
            </button>
            <LanguageSwitcher />
          </div>
        </header>}

        {notFound ? (
          /* ── Not-found empty state ───────────────────────────────────── */
          <div style={{ maxWidth: 720, margin: "0 auto", padding: isMobile ? "60px 14px 80px" : "100px 32px 120px" }}>
            <div style={{
              backgroundColor: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 18,
              padding: isMobile ? "40px 24px" : "60px 40px",
              textAlign: "center",
            }}>
              <div style={{ color: "var(--color-border-strong)", marginBottom: 18, display: "flex", justifyContent: "center" }}>
                <UserIcon />
              </div>
              <div style={{
                fontFamily: FONT.display,
                fontSize: isMobile ? 26 : 32,
                color: C.text,
                letterSpacing: "1px",
                marginBottom: 8,
              }}>
                Athlete Not Found
              </div>
              <div style={{
                fontFamily: FONT.display,
                fontSize: isMobile ? 18 : 22,
                color: C.text2,
                letterSpacing: "0.5px",
                marginBottom: 18,
              }}>
                სპორტსმენი ვერ მოიძებნა
              </div>
              <div style={{
                fontSize: 13,
                color: C.text2,
                lineHeight: 1.7,
                marginBottom: 28,
                fontFamily: FONT.body,
              }}>
                This athlete profile is not published or could not be found.
                <br /><br />
                ამ ID-ით სპორტსმენი ვერ მოიძებნა, ან ჯერ არ აქვს რანჟირების შედეგები.
              </div>
              <button
                className="ap-back"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  background: C.red,
                  color: C.onAccent,
                  border: "none",
                  borderRadius: 8,
                  padding: "12px 24px",
                  cursor: "pointer",
                  fontFamily: FONT.body,
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: "0.4px",
                  transition: "opacity 0.15s",
                }}
                onClick={() => navigate("/athletes")}
              >
                ← {t("nav.athletes")}
              </button>
            </div>
          </div>
        ) : loading ? (
          /* ── Loading skeleton ─────────────────────────────────────────── */
          <div style={{ maxWidth: 900, margin: "0 auto", padding: isMobile ? "20px 14px" : "32px 32px" }}>
            {/* Hero skeleton */}
            <div style={{
              backgroundColor: C.surface, borderRadius: 18,
              border: `1px solid ${C.border}`,
              overflow: "hidden", marginBottom: 28,
            }}>
              <div style={{ height: 4, background: "rgba(218,41,28,0.4)" }} />
              <div style={{ padding: isMobile ? "28px 20px" : "40px 40px", display: "flex", alignItems: "center", gap: 24 }}>
                <div style={{ width: isMobile ? 64 : 96, height: isMobile ? 64 : 96, borderRadius: "50%", background: C.surface2, flexShrink: 0 }} />
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
                  <SkeletonLine w="40%" h={12} />
                  <SkeletonLine w="65%" h={28} />
                  <SkeletonLine w="30%" h={12} />
                </div>
              </div>
            </div>
            {/* Stat cards skeleton */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3,1fr)", gap: 14, marginBottom: 32 }}>
              {[0, 1, 2].map((i) => (
                <div key={i} style={{ backgroundColor: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, padding: "22px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                  <SkeletonLine w="50%" h={36} />
                  <SkeletonLine w="70%" h={10} />
                </div>
              ))}
            </div>
            {/* Table skeleton */}
            <div style={{ backgroundColor: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden" }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderBottom: `1px solid ${C.border}` }}>
                  <SkeletonLine w={120 + i * 20} />
                  <SkeletonLine w={60} />
                  <div style={{ marginLeft: "auto" }}><SkeletonLine w={40} /></div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className={heroLoaded ? "ap-loaded" : ""}>

            {/* ══════════ HERO ══════════════════════════════════════════════ */}
            <div style={{ position: "relative", overflow: "hidden" }}>
              {/* Background */}
              <div style={{
                position: "absolute", inset: 0,
                background: "linear-gradient(135deg, var(--color-surface-2) 0%, var(--color-surface) 60%, rgba(218,41,28,0.08) 100%)",
              }} />
              <div style={{
                position: "absolute", inset: 0,
                backgroundImage: "url('/hero-bg.jpg')",
                backgroundSize: "cover", backgroundPosition: "center",
                opacity: 0.04,
              }} />
              {/* Bottom fade */}
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 80, background: "linear-gradient(to bottom, transparent, var(--color-bg))" }} />

              <div style={{
                position: "relative", zIndex: 2,
                maxWidth: 900, margin: "0 auto",
                padding: isMobile ? "40px 20px 48px" : "56px 40px 64px",
              }}>
                <div className="ap-a0" style={{ marginBottom: 28 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 800, letterSpacing: "2.5px",
                    color: C.red, textTransform: "uppercase", fontFamily: FONT.body,
                  }}>
                    ATHLETE PROFILE
                  </span>
                </div>

                <div className="ap-a1" style={{ display: "flex", alignItems: "center", gap: isMobile ? 18 : 28, flexWrap: "wrap" }}>
                  <ProfileAvatar firstName={firstName} lastName={lastName} imageUrl={imageUrl} size={isMobile ? 72 : 100} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <GeoFlag />
                      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.5px", color: C.text2, fontFamily: FONT.body }}>GEO</span>
                    </div>
                    <div style={{
                      fontFamily: FONT.display,
                      fontSize: isMobile ? "clamp(38px,10vw,52px)" : "clamp(52px,6vw,72px)",
                      letterSpacing: "2px", color: C.text, lineHeight: 0.95,
                      marginBottom: 10,
                    }}>
                      {displayName || "—"}
                    </div>
                    <div style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      backgroundColor: C.surface2,
                      border: `1px solid ${C.border}`,
                      borderRadius: 20, padding: "4px 12px",
                    }}>
                      <ClubIcon />
                      <span style={{ fontSize: 12, fontWeight: 600, color: C.text2, fontFamily: FONT.body }}>
                        {clubName}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ maxWidth: 900, margin: "0 auto", padding: isMobile ? "0 14px 64px" : "0 40px 72px" }}>

              {/* ══════════ STAT CARDS ══════════════════════════════════════ */}
              <div className="ap-a2" style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)",
                gap: 14, marginBottom: 36, marginTop: -20,
              }}>
                {[
                  { label: t("ranking.profile.currentYear"), value: currentYearPoints, color: C.red, Icon: TrophyIcon, sub: `${CURRENT_YEAR} season` },
                  { label: t("ranking.profile.previousYear"), value: previousYearPoints, color: "rgba(96,165,250,0.9)", Icon: StarIcon, sub: `${CURRENT_YEAR - 1} season` },
                  { label: "Best Placement", value: bestPlace !== null ? `#${bestPlace}` : "—", color: C.success, Icon: UserIcon, sub: "all time" },
                ].map((card, i) => (
                  <div key={card.label} style={{
                    backgroundColor: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: 14, padding: isMobile ? "18px 14px" : "24px 20px",
                    textAlign: "center",
                    boxShadow: `0 0 0 0 transparent`,
                  }}>
                    <div style={{ color: "var(--color-border)", display: "flex", justifyContent: "center", marginBottom: 10 }}>
                      <card.Icon />
                    </div>
                    <div style={{
                      fontFamily: FONT.display,
                      fontSize: isMobile ? "clamp(28px,8vw,38px)" : 42,
                      letterSpacing: "1px", lineHeight: 1,
                      color: card.color, marginBottom: 6,
                    }}>
                      {card.value}
                    </div>
                    <div style={{
                      fontSize: 10, fontWeight: 800, letterSpacing: "1px",
                      color: C.text3, textTransform: "uppercase", fontFamily: FONT.body,
                      marginBottom: 2,
                    }}>
                      {card.label}
                    </div>
                    <div style={{ fontSize: 10, color: C.text3, fontFamily: FONT.body }}>
                      {card.sub}
                    </div>
                  </div>
                ))}
              </div>

              {rankingProvenance.length > 0 && <section className="ap-a3" style={{ marginBottom: 32, border: "1px solid var(--color-border)", borderRadius: 14, overflow: "hidden", background: C.surface }}>
                <header style={{ padding: "16px 18px", borderBottom: "1px solid var(--color-border)" }}><h2 style={{ margin: 0, color: C.text, fontFamily: FONT.display, fontSize: 24, letterSpacing: 1 }}>{t("rankingWorkflow.whyTitle")}</h2><p style={{ margin: "5px 0 0", color: "var(--color-text-muted)", fontSize: 12 }}>{t("rankingWorkflow.whyHelp")}</p></header>
                {rankingProvenance.map((row) => <div key={row.id} style={{ display: "flex", justifyContent: "space-between", gap: 14, padding: "13px 18px", borderBottom: `1px solid ${C.border}` }}><div><strong style={{ display: "block", color: C.text, fontSize: 13 }}>{row.sourceLabel || row.editionTitle}</strong><small style={{ color: C.text3 }}>{row.year} · {row.explanation || t(`rankingWorkflow.source.${row.sourceType}`)}</small></div><b style={{ color: row.points >= 0 ? C.success : C.danger }}>{row.points >= 0 ? "+" : ""}{row.points}</b></div>)}
              </section>}

              {/* ══════════ RESULTS TABLES ══════════════════════════════════ */}
              <div className="ap-a3">
                <ResultTable
                  rows={currentYearResults}
                  label={`${CURRENT_YEAR} ${t("ranking.profile.currentYear")} Results`}
                  year={CURRENT_YEAR}
                />
              </div>
              <div className="ap-a4">
                <ResultTable
                  rows={previousYearResults}
                  label={`${CURRENT_YEAR - 1} ${t("ranking.profile.previousYear")} Results`}
                  year={CURRENT_YEAR - 1}
                />
              </div>

              {/* ══════════ OLDER RESULTS (collapsible) ═══════════════════ */}
              {olderYears.length > 0 && (
                <div style={{ marginBottom: 32 }}>
                  <button
                    onClick={() => setShowOlder((v) => !v)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      backgroundColor: C.surface,
                      border: `1px solid ${C.border}`,
                      borderRadius: 10,
                      padding: "12px 16px",
                      cursor: "pointer",
                      color: C.text,
                      fontFamily: FONT.body,
                      fontSize: 13,
                      fontWeight: 700,
                      letterSpacing: "0.4px",
                      transition: "background 0.15s",
                    }}
                    aria-expanded={showOlder}
                  >
                    <span>
                      {showOlder ? "▲ Hide older results" : "▼ Show older results"}
                      <span style={{
                        marginLeft: 10,
                        fontSize: 11,
                        fontWeight: 600,
                        color: C.text3,
                        backgroundColor: C.surface2,
                        border: `1px solid ${C.border}`,
                        borderRadius: 20,
                        padding: "2px 10px",
                      }}>
                        {olderResults.length} result{olderResults.length !== 1 ? "s" : ""} · {olderYears.length} year{olderYears.length !== 1 ? "s" : ""}
                      </span>
                    </span>
                  </button>

                  {showOlder && (
                    <div style={{ marginTop: 18 }}>
                      {olderYears.map((y) => (
                        <ResultTable
                          key={y}
                          rows={olderResults.filter((r) => Number(r.year) === y)}
                          label={`${y} Results`}
                          year={y}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ══════════ FOOTER ══════════════════════════════════════════ */}
              <div style={{
                borderTop: `1px solid ${C.border}`,
                paddingTop: 24, marginTop: 16,
                display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10,
              }}>
                <span style={{ fontSize: 11, color: C.text3, fontFamily: FONT.body }}>
                  © {new Date().getFullYear()} Georgian Taekwondo Federation
                </span>
                <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                  <button onClick={() => navigate("/rankings")} style={{ background: "none", border: "none", color: C.text3, fontSize: 11, cursor: "pointer", fontFamily: FONT.body, padding: 0 }}>
                    Rankings
                  </button>
                  <span style={{ color: "var(--color-border-strong)", fontSize: 11 }}>·</span>
                  <button onClick={() => navigate("/rankings/clubs")} style={{ background: "none", border: "none", color: C.text3, fontSize: 11, cursor: "pointer", fontFamily: FONT.body, padding: 0 }}>
                    Clubs
                  </button>
                  <span style={{ color: "var(--color-border-strong)", fontSize: 11 }}>·</span>
                  <button onClick={() => navigate("/events")} style={{ background: "none", border: "none", color: C.text3, fontSize: 11, cursor: "pointer", fontFamily: FONT.body, padding: 0 }}>
                    {t("landing.events")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default AthleteProfile;
